import React, { useEffect, useRef, useState } from 'react';
import { FormStepDef } from '../../../interfaces/form-config.interface';

interface StepTabsProps {
  steps: FormStepDef[];
  activeStep: number | 'all';
  fieldCounts: Record<number, number>;
  onSelect: (step: number | 'all') => void;
  onReorder: (ordered: FormStepDef[]) => void;
  onRename: (step: FormStepDef, translations: Record<string, string>) => void;
  onAddStep: () => void;
}

interface RenamingState {
  stepNumber: number;
  value: string;
}

const StepTabs = ({
  steps,
  activeStep,
  fieldCounts,
  onSelect,
  onReorder,
  onRename,
  onAddStep,
}: StepTabsProps) => {
  const sorted = [...steps].sort((a, b) => a.sortOrder - b.sortOrder);
  const [reorderEnabled, setReorderEnabled] = useState(false);
  const [dragSrc, setDragSrc] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [renaming, setRenaming] = useState<RenamingState | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const totalCount = Object.values(fieldCounts).reduce((sum, n) => sum + n, 0);

  // Auto-focus and select-all when rename input mounts
  useEffect(() => {
    if (renaming !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  // Turn off reorder mode when user starts renaming
  useEffect(() => {
    if (renaming !== null) setReorderEnabled(false);
  }, [renaming]);

  const handleDrop = (dropIdx: number) => {
    if (!reorderEnabled || dragSrc === null || dragSrc === dropIdx) {
      setDragSrc(null);
      setDragOver(null);
      return;
    }
    const reordered = [...sorted];
    const [moved] = reordered.splice(dragSrc, 1);
    reordered.splice(dropIdx, 0, moved);
    reordered.forEach((s, i) => { s.sortOrder = i; });
    onReorder(reordered);
    setDragSrc(null);
    setDragOver(null);
  };

  const startRename = (e: React.MouseEvent, step: FormStepDef) => {
    e.stopPropagation();
    const label = step.translations?.['en'] ?? `Step ${step.stepNumber}`;
    setRenaming({ stepNumber: step.stepNumber, value: label });
  };

  const commitRename = (step: FormStepDef) => {
    if (renaming === null) return;
    const newValue = renaming.value.trim();
    if (newValue) {
      onRename(step, { ...(step.translations ?? {}), en: newValue });
    }
    setRenaming(null);
  };

  const cancelRename = () => {
    setRenaming(null);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap mb-3">
      {/* All pill */}
      <div
        className={[
          'flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer select-none',
          activeStep === 'all'
            ? 'bg-blue-600 text-white'
            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700',
        ].join(' ')}
        onClick={() => onSelect('all')}
      >
        <span>All</span>
        <span className="text-xs opacity-60">({totalCount})</span>
      </div>

      {/* Step pills */}
      {sorted.map((step, idx) => {
        const label = step.translations?.['en'] ?? `Step ${step.stepNumber}`;
        const count = fieldCounts[step.stepNumber] ?? 0;
        const isActive = activeStep === step.stepNumber;
        const isRenamingThis = renaming?.stepNumber === step.stepNumber;
        const canDrag = reorderEnabled && !isRenamingThis;

        return (
          <div
            key={step.stepNumber}
            draggable={canDrag}
            onDragStart={canDrag ? () => setDragSrc(idx) : undefined}
            onDragEnd={canDrag ? () => { setDragSrc(null); setDragOver(null); } : undefined}
            onDragOver={canDrag ? (e) => { e.preventDefault(); setDragOver(idx); } : undefined}
            onDrop={canDrag ? () => handleDrop(idx) : undefined}
            className={[
              'group relative flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors select-none',
              isRenamingThis ? 'cursor-text' : reorderEnabled ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
              reorderEnabled && dragSrc === idx ? 'opacity-40' : '',
              reorderEnabled && dragOver === idx && dragSrc !== idx ? 'ring-2 ring-amber-400' : '',
              isActive
                ? 'bg-blue-600 text-white'
                : reorderEnabled
                ? 'bg-amber-50 dark:bg-amber-900/20 text-gray-600 dark:text-gray-300 border border-amber-300 dark:border-amber-700'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700',
            ].join(' ')}
            onClick={() => !isRenamingThis && !reorderEnabled && onSelect(step.stepNumber)}
            onDoubleClick={(e) => !reorderEnabled && startRename(e, step)}
          >
            {isRenamingThis ? (
              <input
                ref={inputRef}
                type="text"
                className={[
                  'bg-transparent outline-none border-b min-w-[60px] max-w-[140px] text-sm font-medium',
                  isActive
                    ? 'border-white/60 text-white placeholder-white/50'
                    : 'border-gray-400 dark:border-gray-500 text-gray-800 dark:text-gray-100',
                ].join(' ')}
                value={renaming.value}
                onChange={(e) =>
                  setRenaming((prev) => prev ? { ...prev, value: e.target.value } : null)
                }
                onBlur={() => commitRename(step)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitRename(step);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelRename();
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <>
                {reorderEnabled && (
                  <svg width="8" height="14" viewBox="0 0 12 20" fill="currentColor" className="opacity-50 shrink-0">
                    <circle cx="3" cy="4" r="1.5" />
                    <circle cx="9" cy="4" r="1.5" />
                    <circle cx="3" cy="10" r="1.5" />
                    <circle cx="9" cy="10" r="1.5" />
                    <circle cx="3" cy="16" r="1.5" />
                    <circle cx="9" cy="16" r="1.5" />
                  </svg>
                )}
                <span>{label}</span>
                <span className="text-xs opacity-60">({count})</span>
                {/* Rename pencil (hover, only when not reordering) */}
                {!reorderEnabled && (
                  <button
                    type="button"
                    onClick={(e) => startRename(e, step)}
                    className="hidden group-hover:flex items-center justify-center w-4 h-4 rounded hover:bg-black/10 dark:hover:bg-white/10 shrink-0 opacity-70"
                    title="Rename step"
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-8.378 8.378-3.414.586.586-3.414 8.378-8.378z" />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Reorder toggle button */}
      <button
        type="button"
        onClick={() => setReorderEnabled((v) => !v)}
        title={reorderEnabled ? 'Lock step order' : 'Reorder steps'}
        className={[
          'flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors select-none',
          reorderEnabled
            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 border border-amber-300 dark:border-amber-700'
            : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
        ].join(' ')}
      >
        <svg width="10" height="16" viewBox="0 0 12 20" fill="currentColor">
          <circle cx="3" cy="4" r="1.5" />
          <circle cx="9" cy="4" r="1.5" />
          <circle cx="3" cy="10" r="1.5" />
          <circle cx="9" cy="10" r="1.5" />
          <circle cx="3" cy="16" r="1.5" />
          <circle cx="9" cy="16" r="1.5" />
        </svg>
        {reorderEnabled ? 'Lock' : 'Reorder'}
      </button>

      {/* Add step button (hidden while reordering to avoid confusion) */}
      {!reorderEnabled && (
        <button
          type="button"
          onClick={onAddStep}
          title="Add new step"
          className="w-8 h-8 flex items-center justify-center rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-lg font-light"
        >
          +
        </button>
      )}
    </div>
  );
};

export default StepTabs;
